import { MessageBox } from '@bimser/common';
import { BCFishBone, BCScrollbars } from '@bimser/components';
import { IDictionary, MLHelper, createGuid, isNullOrUndefined } from '@bimser/core';
import { Liquid } from 'liquidjs';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import React from 'react';
import {
	DataSourceType,
	IDataSource,
	IOnLoadArgs,
	MultiLanguageText
} from '../../../../entities/common';
import { getDataSourceData } from '../../../../helper';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import { IFishBoneProperties } from './entities/IFishBoneProperties';
import { IMainReason } from './entities/IMainReason';
import { ISubReason } from './entities/ISubReason';

const liquidEngine = new Liquid();

export default class FishBone extends CompositeDataBoundControl {
	items: IMainReason[];
	maxMainReason: number;
	maxSubReason: number;
	subReasonText: boolean;
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	hierarchyType: 'parentKey' | 'childrenKey' | null;
	childrenKey: string;
	parentKey: string;
	problem: MultiLanguageText;

	constructor() {
		super();

		this.onChangeRealReason = this.onChangeRealReason.bind(this);
		this.onRemoveSubReason = this.onRemoveSubReason.bind(this);
		this.onRemoveMainReason = this.onRemoveMainReason.bind(this);
		this.onChangeMainReason = this.onChangeMainReason.bind(this);
		this.onSelectMainReason = this.onSelectMainReason.bind(this);
		this.addSubReason = this.addSubReason.bind(this);
		this.changeSubReason = this.changeSubReason.bind(this);
		this.onChangeCustomSubReason = this.onChangeCustomSubReason.bind(this);
		this.onNewCustomSubReason = this.onNewCustomSubReason.bind(this);
		this.onChangeProblem = this.onChangeProblem.bind(this);

		this.items = [];
		this.subReasonText = true;
		this.maxMainReason = 6;
		this.maxSubReason = 10;
		this.subReasonText = true;
		this.dataSourceType = 'static';
		this.dataSource = {
			fileId: null,
			displayExpr: [],
			valueExpr: null,
			name: null,
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};

		this.hierarchyType = null;
		this.parentKey = '';
		this.childrenKey = '';
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'problem'];
	}

	override initialize() {
		super.initialize();

		this.problem = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'problem', culture, existCultures);
			}
		);
	}

	override __loadProperties(data: IFishBoneProperties): void {
		super.__loadProperties(data);

		if (data.problem) {
			this.problem.setData(data.problem);
		}
		this.__proxyChanged.items = data.items;
		this.__proxyChanged.maxMainReason = data.maxMainReason;
		this.__proxyChanged.maxSubReason = data.maxSubReason;
		this.__proxyChanged.subReasonText = data.subReasonText;
		this.__proxyChanged.dataSourceType = data.dataSourceType;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.hierarchyType = data.hierarchyType;
		this.__proxyChanged.parentKey = data.parentKey;
		this.__proxyChanged.childrenKey = data.childrenKey;
	}

	getDisplayExpression(item: any, displayFormat = this.dataSource.displayFormat) {
		if (displayFormat.includes('.__userCulture')) {
			displayFormat = displayFormat.replace(
				'.__userCulture',
				`['${this.__form.currentLanguage.culture}']`
			);
		}

		return liquidEngine.parseAndRenderSync(displayFormat, item);
	}

	override __onLoad({ externalData, forceRefresh, reload }: IOnLoadArgs = {}): void {
		if (this.__form.displayMode === 'preview') {
			return;
		}

		if (this.dataSourceType === 'dynamic') {
			this.__proxyChanging.loading = true;
			if (this.subReasonText) {
				return this.loadOnlyMainReasons({ externalData, forceRefresh, reload });
			}
			if (this.hierarchyType) {
				return this.loadMainAndSubReasons({ externalData, forceRefresh, reload });
			}
		}

		if (this.dataSourceType === 'static') {
			const dynamicMainReasons =
				this.items?.filter(item => item.dataSourceType === 'dynamic') || [];

			if (dynamicMainReasons.length) {
				this.__proxyChanging.loading = true;
				this.loadSubReasonsForMainReasons(dynamicMainReasons)
					.then(() => {
						this.__proxyChanging.loading = false;
					})
					.catch(() => {
						this.__proxyChanging.loading = false;
					});
			}
		}
	}

	async loadSubReasonsForMainReasons(dynamicMainReasons: IMainReason[]) {
		for (const mainReason of dynamicMainReasons) {
			const res = await getDataSourceData({
				dataSource: mainReason.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				loadOptions: { pagination: { skip: 0, take: 100 }, sorts: null, filters: [] },
				projectVersion: this.__form.packageVersion
			});
			const data = res?.data?.result;

			if (data) {
				const subReasons = data.map(x => {
					const key = x[mainReason.dataSource.valueExpr].toString();

					return {
						key,
						realReason: x.realReason,
						selectedIndex:
							mainReason.subReasons?.find(sub => sub.key === key)?.selectedIndex ||
							(x.selectedIndex as number) ||
							null,
						text: {
							[MLHelper.currentLanguage.culture]: this.getDisplayExpression(
								x,
								mainReason.dataSource.displayFormat
							)
						}
					};
				});

				const items = this.items.map(item =>
					item.key === mainReason.key ? { ...item, subReasons } : item
				);

				this.__proxyChanging.items = items;
			}
		}
	}

	loadOnlyMainReasons({ externalData, forceRefresh, reload }: IOnLoadArgs = {}) {
		getDataSourceData({
			dataSource: this.dataSource,
			projectName: this.__form.projectName,
			clientCacheId: this.__form.__windowId,
			fieldParams: externalData?.fieldParams,
			reload,
			loadOptions: {
				pagination: { skip: 0, take: 100 },
				sorts: null,
				filters: []
			},
			forceRefresh,
			projectVersion: this.__form.packageVersion
		})
			.then(res => {
				if (!res?.data?.result) {
					this.__proxyChanging.loading = false;
					return;
				}

				const data = res.data.result;

				const tId = this.__form.beginTransaction();

				this.__proxyChanging.items = data.map<IMainReason>(x => ({
					key: x[this.dataSource.valueExpr].toString(),
					selectedIndex: (x.selectedIndex as number) || null,
					subReasons: [],
					text: {
						[MLHelper.currentLanguage.culture]: this.getDisplayExpression(x)
					}
				}));

				this.__form.endTransaction(tId).then(() => {
					this.__proxyChanging.loading = false;
				});
			})
			.catch(() => {
				this.__proxyChanging.loading = false;
			});
	}

	loadMainAndSubReasons({ externalData, forceRefresh, reload }: IOnLoadArgs = {}) {
		getDataSourceData({
			dataSource: this.dataSource,
			projectName: this.__form.projectName,
			clientCacheId: this.__form.__windowId,
			fieldParams: externalData?.fieldParams,
			reload,
			loadOptions: {
				pagination: { skip: 0, take: 300 },
				sorts: null,
				filters: []
			},
			forceRefresh,
			projectVersion: this.__form.packageVersion
		})
			.then(res => {
				if (!res?.data?.result) {
					this.__proxyChanging.loading = false;
					return;
				}

				const data = res.data.result;
				const tId = this.__form.beginTransaction();

				if (this.hierarchyType === 'childrenKey') {
					this.__proxyChanging.items = data.map(x => {
						const key = x[this.dataSource.valueExpr].toString();
						const currentMainReason = this.items.find(item => item.key === key);
						const newMainReason = {
							key: x[this.dataSource.valueExpr].toString(),
							selectedIndex: x.selectedIndex,
							subReasons: this.convertSubReasons(x[this.childrenKey]),
							text: {
								[MLHelper.currentLanguage.culture]: this.getDisplayExpression(x)
							}
						};

						if (currentMainReason) {
							return merge(newMainReason, currentMainReason);
						}
						return newMainReason;
					});
				} else {
					const mainReasons = data.filter(
						x =>
							isNullOrUndefined(x[this.parentKey]) ||
							x[this.parentKey] === '0' ||
							x[this.parentKey] === 0
					);

					this.__proxyChanging.items = mainReasons.map(x => {
						const key = x[this.dataSource.valueExpr].toString();
						const currentMainReason = this.items.find(item => item.key === key);
						const newMainReason = {
							key,
							selectedIndex: x.selectedIndex,
							subReasons: this.convertSubReasons(
								data.filter(e => e[this.parentKey] === x[this.dataSource.valueExpr])
							),
							text: {
								[MLHelper.currentLanguage.culture]: this.getDisplayExpression(x)
							}
						};

						if (currentMainReason) {
							return merge(newMainReason, currentMainReason);
						}
						return newMainReason;
					});
				}

				this.__form.endTransaction(tId).then(() => {
					this.__proxyChanging.loading = false;
				});
			})
			.catch(err => {
				this.__proxyChanging.loading = false;
			});
	}

	convertSubReasons(subReasons: any[] = []) {
		return subReasons.map(x => ({
			key: x[this.dataSource.valueExpr].toString(),
			realReason: x.realReason,
			selectedIndex: (x.selectedIndex as number) || null,
			text: { [MLHelper.currentLanguage.culture]: x.text }
		}));
	}

	onChangeRealReason(args: { mainReasonKey: string; subReason: ISubReason }) {
		this.__proxyChanging.items = this.items.map(mainReason => {
			if (mainReason.key === args.mainReasonKey) {
				return {
					...mainReason,
					subReasons: mainReason.subReasons.map(subReason => ({
						...subReason,
						realReason: args.subReason.key === subReason.key
					}))
				};
			}

			return mainReason;
		});
	}

	onRemoveSubReason(args: { mainReasonKey: string; subReason: ISubReason }) {
		let subReasonFound = false;

		this.__proxyChanging.items = this.items.map(mainReason => {
			if (mainReason.key === args.mainReasonKey) {
				const sorted = cloneDeep(mainReason.subReasons).sort(
					(a, b) => a.selectedIndex - b.selectedIndex
				);
				return {
					...mainReason,
					subReasons: sorted.map<ISubReason>(subReason => {
						if (subReason.key === args.subReason.key) {
							subReasonFound = true;
							return { ...subReason, selectedIndex: null, realReason: false };
						}

						if (subReasonFound) {
							return {
								...subReason,
								selectedIndex: !isNullOrUndefined(subReason.selectedIndex)
									? subReason.selectedIndex - 1
									: subReason.selectedIndex
							};
						}

						return subReason;
					})
				};
			}

			return mainReason;
		});
	}

	onRemoveMainReason(args: { mainReasonKey: string }) {
		let mainReasonFound = false;

		this.__proxyChanging.items = this.items
			.sort((a, b) => a.selectedIndex - b.selectedIndex)
			.map(mainReason => {
				if (mainReason.key === args.mainReasonKey) {
					mainReasonFound = true;

					return { ...mainReason, selectedIndex: null };
				}

				if (mainReasonFound) {
					return {
						...mainReason,
						selectedIndex: !isNullOrUndefined(mainReason.selectedIndex)
							? mainReason.selectedIndex - 1
							: mainReason.selectedIndex
					};
				}

				return mainReason;
			});
	}

	onChangeMainReason(args: { oldMainReasonKey: string; newMainReasonKey: string }) {
		const oldMainReasonIndex = this.items
			.find(({ key }) => key === args.oldMainReasonKey)
			.selectedIndex.valueOf();

		this.__proxyChanging.items = this.items.map(i => {
			if (i.key === args.oldMainReasonKey) {
				return { ...i, selectedIndex: null };
			} else if (i.key === args.newMainReasonKey) {
				return { ...i, selectedIndex: oldMainReasonIndex };
			}
			return i;
		});
	}

	onSelectMainReason(args: { newMainReasonKey: string }) {
		const maxIndex = this.items.reduce(
			(p, c) => (c.selectedIndex > p ? c.selectedIndex : p ?? c.selectedIndex),
			0
		);

		this.__proxyChanging.items = this.items.map(i => {
			if (i.key === args.newMainReasonKey) {
				return { ...i, selectedIndex: maxIndex + 1 };
			}
			return i;
		});
	}

	addSubReason(args: IMainReason) {
		const { close } = this.__form.__interactionHelper({
			id: 'SUB_REASON_LOOKUP',
			type: 'modal',
			title: 'Sub Reasons',
			width: 'large',
			contentComponentType: 'SubReasonLookup',
			contentComponentProps: {
				onOk: (_subReason: ISubReason) => {
					this.__proxyChanging.items = this.items.map<IMainReason>(mainReason => {
						if (mainReason.key === args.key) {
							const maxIndex = mainReason.subReasons.reduce(
								(p, c) =>
									c.selectedIndex > p ? c.selectedIndex : p ?? c.selectedIndex,
								0
							);

							return {
								...mainReason,
								subReasons: mainReason.subReasons.map<ISubReason>(subReason => {
									if (subReason.key === _subReason.key) {
										return { ...subReason, selectedIndex: maxIndex + 1 };
									}
									return subReason;
								})
							};
						}

						return mainReason;
					});

					MessageBox.closeModal('SUB_REASON_LOOKUP');
				},
				data: args.subReasons.filter(({ selectedIndex }) =>
					isNullOrUndefined(selectedIndex)
				)
			},
			modalProps: {
				buttons: []
			},
			onOk: () => {
				close();
			}
		});
	}

	changeSubReason(args: { mainReasonKey: string; subReason: ISubReason }) {
		const { close } = this.__form.__interactionHelper({
			id: 'SUB_REASON_LOOKUP',
			type: 'modal',
			title: 'Sub Reasons',
			width: 'large',
			contentComponentType: 'SubReasonLookup',
			contentComponentProps: {
				onOk: (newSubReason: ISubReason) => {
					this.__proxyChanging.items = this.items.map(mainReason => {
						if (mainReason.key === args.mainReasonKey) {
							return {
								...mainReason,
								subReasons: mainReason.subReasons.map<ISubReason>(subReason => {
									if (subReason.key === args.subReason.key) {
										return {
											...subReason,
											selectedIndex: null,
											realReason: false
										};
									}

									if (subReason.key === newSubReason.key) {
										return {
											...subReason,
											selectedIndex: args.subReason.selectedIndex
										};
									}

									return subReason;
								})
							};
						}

						return mainReason;
					});

					close();
				},
				data: this.items
					.find(mainReason => mainReason.key === args.mainReasonKey)
					.subReasons.filter(({ selectedIndex }) => isNullOrUndefined(selectedIndex))
			},
			modalProps: {
				buttons: []
			},
			onOk: () => {
				close();
			}
		});
	}

	onChangeCustomSubReason(args: {
		data: IDictionary<string>;
		mainReasonKey: string;
		subReasonKey: string;
	}) {
		this.__proxyChanging.items = this.items.map(mainReason => {
			if (mainReason.key === args.mainReasonKey) {
				return {
					...mainReason,
					subReasons: mainReason.subReasons.map<ISubReason>(subReason => {
						if (subReason.key === args.subReasonKey) {
							return { ...subReason, text: { ...args.data } };
						}
						return subReason;
					})
				};
			}

			return mainReason;
		});
	}

	onNewCustomSubReason(args: { data: IDictionary<string>; mainReasonKey: string }) {
		this.__proxyChanging.items = this.items.map(mainReason => {
			if (mainReason.key === args.mainReasonKey) {
				const maxIndex = mainReason.subReasons.reduce(
					(p, c) => (c.selectedIndex > p ? c.selectedIndex : p ?? c.selectedIndex),
					0
				);

				return {
					...mainReason,
					subReasons: [
						...mainReason.subReasons,
						{
							key: createGuid(),
							text: args.data,
							realReason: false,
							selectedIndex: maxIndex + 1
						}
					]
				};
			}

			return mainReason;
		});
	}

	onChangeProblem(culture: string, val: string) {
		this.problem.setText(val, culture);
	}

	override __render(children?: React.ReactNode) {
		const scrollOptions = {
			styles: { maxHeight: `${Math.max(this.__form.windowSize.height, 200)}px` }
		};

		if (this.__form.displayMode === 'preview') {
			return super.__render(
				<BCScrollbars {...scrollOptions}>
					<BCFishBone
						mainReasons={this.items as any}
						problem={this.problem}
						customSubReason={this.subReasonText}
						maxMainReason={this.maxMainReason}
						maxSubReason={this.maxSubReason}
						backgroundColor={this.style.backgroundColor}
						disabled={this.__isDisabled()}
						readonly={this.__isReadOnly()}
					/>
				</BCScrollbars>
			);
		}

		return super.__render(
			<BCScrollbars {...scrollOptions}>
				<BCFishBone
					mainReasons={this.items as any}
					problem={this.problem}
					customSubReason={this.subReasonText}
					maxMainReason={this.maxMainReason}
					maxSubReason={this.maxSubReason}
					backgroundColor={this.style.backgroundColor}
					onChangeRealReason={this.onChangeRealReason}
					onChangeMainReason={this.onChangeMainReason}
					onRemoveSubReason={this.onRemoveSubReason}
					onRemoveMainReason={this.onRemoveMainReason}
					onSelectMainReason={this.onSelectMainReason}
					addSubReason={this.addSubReason}
					changeSubReason={this.changeSubReason}
					onChangeCustomSubReason={this.onChangeCustomSubReason}
					onNewCustomSubReason={this.onNewCustomSubReason}
					onChangeProblem={this.onChangeProblem}
					disabled={this.__isDisabled()}
					readonly={this.__isReadOnly()}
				/>
			</BCScrollbars>
		);
	}
}
